import { useMemo } from "react";
import { KpiCard } from "@/components/kit";
import { fmtHM, fmtSignedHM, fmtPct, fmtClock, distribution, monthlyRollup, absDowClustering, ACCENT } from "./adhUtils";

const card = "mk-card p-5";
const label = "font-mono-hud text-[10px] uppercase tracking-[0.25em] text-thdim";

const Tile = ({ id, l, v, sub, color }) => (
  <KpiCard testid={`adh-kpi-${id}`} label={l} value={v} sub={sub} color={color || "#7a8fb5"} big />
);

export default function AdhOverview({ data, slice, kpis }) {
  const t = data.targets;
  const dist = useMemo(() => distribution(slice, t), [slice, t]);
  const monthly = useMemo(() => monthlyRollup(slice, t), [slice, t]);
  const dow = useMemo(() => absDowClustering(slice), [slice]);
  const dowTotal = Object.values(dow).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Tile id="met" l={`Days meeting ${t.hours}h`} v={fmtPct(kpis.pctMet)} sub={`${kpis.validDays} valid attended days`} color={kpis.pctMet >= 60 ? "#10B981" : kpis.pctMet >= 40 ? "#F59E0B" : "#F43F5E"} />
        <Tile id="avg" l="Average time in office" v={fmtHM(kpis.avgMin)} sub={`median ${fmtHM(kpis.medMin)}`} color="#2563EB" />
        <Tile id="deficit" l={`Net vs ${t.hours}h`} v={fmtSignedHM(kpis.deficit)} sub="surplus / deficit across range" color={kpis.deficit >= 0 ? "#10B981" : "#F43F5E"} />
        <Tile id="late" l="Late beyond grace" v={fmtPct(kpis.latePct)} sub={`median in ${fmtClock(kpis.medIn)} · out ${fmtClock(kpis.medOut)}`} color={kpis.latePct <= 20 ? "#10B981" : kpis.latePct <= 40 ? "#F59E0B" : "#F43F5E"} />
        <Tile id="attendance" l="Mon–Fri attendance" v={fmtPct(kpis.monFriPct)} sub={`${kpis.wkAbs} weekday absences · ${kpis.corrupt} corrupt punches`} color={kpis.monFriPct >= 90 ? "#10B981" : kpis.monFriPct >= 75 ? "#F59E0B" : "#F43F5E"} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className={card} data-testid="adh-distribution">
          <p className={label}>Time-in-office distribution · valid days</p>
          <div className="mt-4 space-y-2.5">
            {dist.map((b) => (
              <div key={b.label} className="flex items-center gap-3">
                <span className="w-40 shrink-0 text-xs text-thbody">{b.label}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-thhover/10">
                  <div className="h-full rounded-full" style={{ width: `${b.pct}%`, background: ACCENT }} />
                </div>
                <span className="w-16 shrink-0 text-right font-mono-hud text-xs text-thbody">{b.n} · {Math.round(b.pct)}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className={card} data-testid="adh-dow-clustering">
          <p className={label}>Weekday absence clustering</p>
          <div className="mt-4 space-y-2.5">
            {Object.entries(dow).map(([d, n]) => (
              <div key={d} className="flex items-center gap-3">
                <span className="w-40 shrink-0 text-xs text-thbody">{d}</span>
                <div className="h-3 flex-1 overflow-hidden rounded-full bg-thhover/10">
                  <div className="h-full rounded-full" style={{ width: `${(100 * n) / dowTotal}%`, background: ["Mon", "Fri"].includes(d) ? "#F43F5E" : "#F59E0B" }} />
                </div>
                <span className="w-16 shrink-0 text-right font-mono-hud text-xs text-thbody">{n}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-relaxed text-thmute">
            Mon + Fri = {fmtPct((100 * (dow.Mon + dow.Fri)) / dowTotal)} of weekday absences — above ~40% signals a long-weekend pattern.
          </p>
        </div>
      </div>

      <div className={`${card} p-0`} data-testid="adh-monthly-table">
        <p className={`${label} px-5 pt-4`}>Month-by-month trend</p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-left">
            <thead><tr className="border-b border-thborder/10">
              {["Month", "Present", "Absent", "Weekday abs.", "Median in", "Median out", "Median hours", `Days ≥ ${t.hours}h`, "Late %"].map((h, i) => (
                <th key={h} className={`font-mono-hud px-4 py-2.5 text-[9px] uppercase tracking-[0.12em] text-thdim ${i ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {monthly.map((m) => (
                <tr key={m.m} className="border-b border-thborder/5">
                  <td className="px-4 py-2.5 text-xs font-medium text-thstrong">{m.m}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{m.present}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{m.absent}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-[#F59E0B]">{m.wkAbs}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtClock(m.medIn)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtClock(m.medOut)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtHM(m.medMin)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(m.pctMet)}</td>
                  <td className="px-4 py-2.5 text-right font-mono-hud text-xs text-thbody">{fmtPct(m.latePct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
